import { useCreation } from 'ahooks'
import { isNil, last, snakeCase } from 'lodash'

import { pick } from '@eastcoal/common/utils/object'

import { GenericalRecord } from '../../../types/utilities'
import { toArray } from '../../../utils/array'
import { DataEmpty } from '../../DataEmpty'
import { DataError } from '../../DataError'
import { ProTable } from '../../ProTable'
import { ProTableProps } from '../../ProTable/interface'
import { useRemoteTableStore } from '../Store/hooks/useStore'

import { RemoteTableCoreProps, RemoteTableContext } from './interface'

export const RemoteTableCore = <T extends GenericalRecord, S>({
  className,
  context: externalContext,
  pagination: externalPagination,
  rowSelection: externalSelection,
  selectRowMultiple,
  ...restProps
}: RemoteTableCoreProps<T, S>) => {
  const {
    data,
    error,
    loading,
    pagination: innerPagination,
    selectedRowKeys,
    setSelectedRows,
    requestParams,
    doRequest,
    reload
  } = useRemoteTableStore(state =>
    pick(state, [
      'data',
      'error',
      'loading',
      'pagination',
      'selectedRowKeys',
      'setSelectedRows',
      'requestParams',
      'doRequest',
      'reload'
    ])
  )

  const context = useCreation(
    () =>
      ({
        ...externalContext,
        reload
      }) as RemoteTableContext<S>,
    [externalContext, reload]
  )

  const emptyText = error ? (
    <DataError error={error} reload={reload} />
  ) : (
    <DataEmpty reload={reload} />
  )

  const innerSelectionOnChange = (selectedRows: T[]) => {
    if (selectRowMultiple) {
      setSelectedRows(selectedRows)
    } else {
      if (selectedRows.length > 0) {
        setSelectedRows([last(selectedRows) as T])
      } else {
        setSelectedRows(selectedRows)
      }
    }
  }

  const rowSelection: RemoteTableCoreProps<T, S>['rowSelection'] =
    externalSelection
      ? {
          selectedRowKeys,
          preserveSelectedRowKeys: true,
          ...externalSelection,
          onChange(_, selectedRows, info) {
            innerSelectionOnChange(selectedRows)
            externalSelection?.onChange?.(selectedRowKeys, selectedRows, info)
          }
        }
      : isNil(selectRowMultiple)
        ? undefined
        : {
            selectedRowKeys,
            hideSelectAll: !selectRowMultiple,
            onChange(_, selectedRows) {
              innerSelectionOnChange(selectedRows)
            }
          }

  const onChange: ProTableProps<T, RemoteTableContext<S>>['onChange'] = (
    pagination,
    filters,
    sorter,
    extra
  ) => {
    const sorters = toArray(sorter)
      .filter(item => !!item.column?.dataIndex)
      .map(item => ({
        dataIndex: snakeCase(item.column?.dataIndex as string),
        order: item.order
      }))
    const orders: Array<{ column: string; asc: boolean }> =
      requestParams.orders || []
    const filterOrders = orders.filter(item =>
      sorters.every(sorter => sorter.dataIndex !== item.column)
    )
    const sortOrders = sorters.map(sorter => ({
      column: sorter.dataIndex,
      asc: sorter.order === 'ascend'
    }))

    doRequest({
      ...requestParams,
      current: pagination.current,
      pageSize: pagination.pageSize,
      orders: [...filterOrders, ...sortOrders]
    })
  }

  const tableProps: ProTableProps<T, RemoteTableContext<S>> = {
    bordered: true,
    ...restProps,
    context,
    rowSelection,
    loading,
    dataSource: data,
    pagination:
      externalPagination === false
        ? false
        : {
            ...externalPagination,
            ...innerPagination
          },
    locale: {
      emptyText
    },
    onChange
  }

  return <ProTable {...tableProps} rowSelection={rowSelection} />
}
